import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Trash2, FileText, Pencil, Star, CheckCircle, XCircle, AlertCircle, Send, Download,
  Users, ChevronRight, X, UserCheck, CheckSquare, RotateCcw, Archive, UserPlus, ExternalLink, Loader2
} from 'lucide-react';

const formatTestTime = (seconds) => {
  if (seconds == null) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s > 0 ? `${s}s` : ''}`.trim() : `${s}s`;
};

const NEXT_ACTION_STYLES = {
  review:       { bg: 'bg-yellow-100 text-yellow-800 border-yellow-200',   dot: 'bg-yellow-400' },
  close_review: { bg: 'bg-orange-100 text-orange-800 border-orange-200',   dot: 'bg-orange-400' },
  send_test:    { bg: 'bg-purple-100 text-purple-800 border-purple-200',   dot: 'bg-purple-400' },
  awaiting_test:{ bg: 'bg-blue-100 text-blue-800 border-blue-200',         dot: 'bg-blue-400' },
  decide:       { bg: 'bg-indigo-100 text-indigo-800 border-indigo-200',   dot: 'bg-indigo-400' },
  send_outcome: { bg: 'bg-green-100 text-green-800 border-green-200',      dot: 'bg-green-400' },
  done:         { bg: 'bg-gray-100 text-gray-500 border-gray-200',         dot: 'bg-gray-300' },
};

const STATUS_COLORS = {
  received:        'bg-blue-100 text-blue-800',
  ineligible:      'bg-red-100 text-red-700',
  shortlisted:     'bg-yellow-100 text-yellow-800',
  not_shortlisted: 'bg-gray-100 text-gray-600',
  test_invited:    'bg-purple-100 text-purple-800',
  test_completed:  'bg-indigo-100 text-indigo-800',
  accepted:        'bg-green-100 text-green-800',
  selected_tba:    'bg-violet-100 text-violet-800',
  not_selected:    'bg-red-100 text-red-700',
  on_hold:         'bg-amber-100 text-amber-700',
  enrolled:        'bg-teal-100 text-teal-800',
  withdrawn:       'bg-slate-100 text-slate-600',
  // legacy
  pending:         'bg-yellow-100 text-yellow-800',
  screening:       'bg-blue-100 text-blue-800',
  test_sent:       'bg-purple-100 text-purple-800',
  rejected:        'bg-red-100 text-red-700',
};

const STATUS_LABELS = {
  received:        'Received',
  ineligible:      'Ineligible',
  shortlisted:     'Shortlisted',
  not_shortlisted: 'Not Shortlisted',
  test_invited:    'Assessment Invited',
  test_completed:  'Assessment Completed',
  accepted:        'Accepted',
  selected_tba:    'Selected — TBA',
  not_selected:    'Not Selected',
  on_hold:         'On Hold',
  enrolled:        'Enrolled',
  withdrawn:       'Withdrawn',
  // legacy
  pending:         'Pending',
  screening:       'Screening',
  test_sent:       'Test Sent',
  rejected:        'Rejected',
};

export default function AdminApplicationsPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [applications, setApplications] = useState([]);
  const [intakePeriods, setIntakePeriods] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIntakeId, setSelectedIntakeId] = useState(location.state?.intakeId || 'all');
  const [intakeStats, setIntakeStats] = useState(null);

  const [filterMode, setFilterMode] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [pipelineStats, setPipelineStats] = useState(null);
  const [testSummaries, setTestSummaries] = useState({});

  const [selectedAppIds, setSelectedAppIds] = useState(new Set());

  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ id: '', email: '', status: '', score: '', final_score: '', app_score_display: '' });

  const [bulkDecisionDialog, setBulkDecisionDialog] = useState(false);
  const [pendingDecision, setPendingDecision] = useState(null);
  const [processingDecision, setProcessingDecision] = useState(false);

  const [sendTestDialog, setSendTestDialog] = useState(false);
  const [sendingTestSelected, setSendingTestSelected] = useState(false);

  const [autoShortlistDialog, setAutoShortlistDialog] = useState(false);
  const [autoShortlistCutoff, setAutoShortlistCutoff] = useState('75');
  const [autoShortlisting, setAutoShortlisting] = useState(false);
  const [bulkSendingTestAll, setBulkSendingTestAll] = useState(false);
  const [bulkEnrollDialog, setBulkEnrollDialog] = useState(false);
  const [bulkEnrollCohort, setBulkEnrollCohort] = useState('');
  const [bulkEnrolling, setBulkEnrolling] = useState(false);

  const [assignIntakeDialog, setAssignIntakeDialog] = useState(false);
  const [assignIntakeId, setAssignIntakeId] = useState('');
  const [assigningIntake, setAssigningIntake] = useState(false);

  const [testDialog, setTestDialog] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);

  const [closeReviewDialog, setCloseReviewDialog] = useState(false);
  const [closingReview, setClosingReview] = useState(false);
  const [crSkipShortlisted, setCrSkipShortlisted] = useState(true);
  const [crSkipNotShortlisted, setCrSkipNotShortlisted] = useState(true);

  const [activeTab, setActiveTab] = useState('applications'); // 'applications' | 'archived' | 'reinvite_pool'
  const [archivedApps, setArchivedApps] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);

  // Re-invite pool
  const [pool, setPool] = useState([]);
  const [poolLoading, setPoolLoading] = useState(false);
  const [poolActionId, setPoolActionId] = useState(null);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const downloadCsv = async (url, filename) => {
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error('Export failed');
    }
  };

  useEffect(() => { fetchSupportingThenApplications(); }, []);

  const fetchSupportingThenApplications = async () => {
    try {
      const [intakesRes, cohortsRes] = await Promise.all([
        axios.get(`${API}/intake-periods`, auth()),
        axios.get(`${API}/cohorts`, auth()),
      ]);
      const intakes = intakesRes.data;
      setIntakePeriods(intakes);
      setCohorts(cohortsRes.data);

      // Read URL query params first (from pipeline page navigation)
      const qp = new URLSearchParams(location.search);
      const qpIntakeId = qp.get('intake_id');
      const qpStatus = qp.get('status');
      const qpFlagged = qp.get('flagged');

      if (qpIntakeId) {
        setSelectedIntakeId(qpIntakeId);
        if (qpFlagged === 'true') setFilterMode('flagged');
        else if (qpStatus) setFilterMode(qpStatus);
        fetchApplications(qpIntakeId);
        return;
      }

      // Always default to the active intake (open or reviewing), ignore prior navigation state
      const activeIntake = intakes.find(i => !['draft', 'closed'].includes(i.status));
      if (activeIntake) {
        setSelectedIntakeId(activeIntake.id);
        fetchApplications(activeIntake.id);
        return;
      }

      fetchApplications('all');
    } catch {
      fetchApplications('all');
    }
  };

  const fetchApplications = async (intakeId) => {
    setLoading(true);
    try {
      const url = intakeId && intakeId !== 'all'
        ? `${API}/applications?intake_id=${intakeId}`
        : `${API}/applications`;
      const summariesUrl = intakeId && intakeId !== 'all'
        ? `${API}/aptitude-tests/summaries?intake_id=${intakeId}`
        : `${API}/aptitude-tests/summaries`;

      if (intakeId && intakeId !== 'all') {
        const [res, statsRes, pipelineRes, summariesRes] = await Promise.all([
          axios.get(url, auth()),
          axios.get(`${API}/intake-periods/${intakeId}/stats`, auth()),
          axios.get(`${API}/applications/pipeline-stats?intake_id=${intakeId}`, auth()),
          axios.get(summariesUrl, auth()),
        ]);
        setApplications(res.data);
        setIntakeStats(statsRes.data);
        setPipelineStats(pipelineRes.data);
        setTestSummaries(summariesRes.data);
      } else {
        const [res, pipelineRes, summariesRes] = await Promise.all([
          axios.get(url, auth()),
          axios.get(`${API}/applications/pipeline-stats`, auth()),
          axios.get(summariesUrl, auth()),
        ]);
        setApplications(res.data);
        setIntakeStats(null);
        setPipelineStats(pipelineRes.data);
        setTestSummaries(summariesRes.data);
      }
      setSelectedAppIds(new Set());
    } catch {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleIntakeSelect = (intakeId) => {
    setSelectedIntakeId(intakeId);
    setFilterMode('all');
    fetchApplications(intakeId);
    if (activeTab === 'reinvite_pool') fetchPool(intakeId);
  };

  const handleDelete = async (appId) => {
    if (!window.confirm('Archive this application? It can be restored from the Archived tab.')) return;
    try {
      await axios.delete(`${API}/applications/${appId}`, auth());
      toast.success('Application archived');
      fetchApplications(selectedIntakeId);
    } catch {
      toast.error('Failed to archive application');
    }
  };

  const handleWithdraw = async (app) => {
    if (!window.confirm(`Mark ${app.full_name} as Withdrawn? This will remove them from their cohort.`)) return;
    try {
      await axios.patch(`${API}/applications/${app.id}/withdraw`, {}, auth());
      toast.success(`${app.full_name} marked as withdrawn`);
      fetchApplications(selectedIntakeId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to withdraw applicant');
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedAppIds);
    if (!ids.length) return;
    if (!window.confirm(`Archive ${ids.length} selected application${ids.length > 1 ? 's' : ''}? They can be restored from the Archived tab.`)) return;
    try {
      await axios.delete(`${API}/applications/bulk`, { ...auth(), data: { ids } });
      toast.success(`${ids.length} application${ids.length > 1 ? 's' : ''} archived`);
      fetchApplications(selectedIntakeId);
    } catch {
      toast.error('Failed to archive applications');
    }
  };

  const fetchArchivedApps = async () => {
    setArchivedLoading(true);
    try {
      const url = selectedIntakeId && selectedIntakeId !== 'all'
        ? `${API}/applications/archived?intake_id=${selectedIntakeId}`
        : `${API}/applications/archived`;
      const res = await axios.get(url, auth());
      setArchivedApps(res.data);
    } catch {
      toast.error('Failed to load archived applications');
    } finally {
      setArchivedLoading(false);
    }
  };

  const fetchPool = async (intakeId) => {
    const id = intakeId || selectedIntakeId;
    if (!id || id === 'all') { setPool([]); return; }
    setPoolLoading(true);
    try {
      const res = await axios.get(`${API}/intakes/${id}/reinvite-pool`, auth());
      setPool(res.data);
    } catch {
      toast.error('Failed to load re-invite pool');
    } finally {
      setPoolLoading(false);
    }
  };

  const duplicatePoolEntry = async (poolId, name) => {
    if (!window.confirm(`Duplicate ${name}'s application into this intake?`)) return;
    setPoolActionId(poolId);
    try {
      const res = await axios.post(`${API}/reinvite-pool/${poolId}/duplicate`, {}, auth());
      toast.success(`Application duplicated — status: ${res.data.status}`);
      fetchPool();
      navigate(`/admin/applications/${res.data.new_application_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to duplicate application');
    } finally {
      setPoolActionId(null);
    }
  };

  const declinePoolEntry = async (poolId) => {
    setPoolActionId(poolId);
    try {
      await axios.patch(`${API}/reinvite-pool/${poolId}`, { status: 'declined' }, auth());
      toast.success('Candidate declined from pool');
      fetchPool();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to decline');
    } finally {
      setPoolActionId(null);
    }
  };

  const undeclinePoolEntry = async (poolId) => {
    setPoolActionId(poolId);
    try {
      await axios.patch(`${API}/reinvite-pool/${poolId}`, { status: 'awaiting_review' }, auth());
      toast.success('Candidate restored to pool');
      fetchPool();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to restore');
    } finally {
      setPoolActionId(null);
    }
  };

  const handleRestore = async (archiveId) => {
    if (!window.confirm('Restore this application back to the active list?')) return;
    try {
      await axios.post(`${API}/applications/${archiveId}/restore`, {}, auth());
      toast.success('Application restored');
      fetchArchivedApps();
      fetchApplications(selectedIntakeId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to restore application');
    }
  };

  const handlePermanentDelete = async (archiveId) => {
    if (!window.confirm('Permanently delete this application? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/applications/${archiveId}/permanent`, auth());
      toast.success('Application permanently deleted');
      fetchArchivedApps();
    } catch {
      toast.error('Failed to delete application');
    }
  };

  const handleEditSave = async () => {
    try {
      const payload = {};
      if (editForm.email) payload.email = editForm.email;
      if (editForm.status) payload.status = editForm.status;
      if (editForm.score !== '') payload.score = parseFloat(editForm.score);
      if (editForm.final_score !== '') payload.final_score = parseFloat(editForm.final_score);
      await axios.patch(`${API}/applications/${editForm.id}`, payload, auth());
      toast.success('Application updated');
      setEditDialog(false);
      fetchApplications(selectedIntakeId);
    } catch {
      toast.error('Failed to update application');
    }
  };

  const handleToggleShortlist = async (appId, currentValue) => {
    const newValue = currentValue === true ? false : true;
    try {
      await axios.patch(`${API}/applications/${appId}/shortlist?shortlisted=${newValue}`, {}, auth());
      setApplications(prev => prev.map(a => a.id === appId ? { ...a, shortlisted: newValue } : a));
      if (selectedIntakeId && selectedIntakeId !== 'all') {
        const statsRes = await axios.get(`${API}/intake-periods/${selectedIntakeId}/stats`, auth());
        setIntakeStats(statsRes.data);
      }
    } catch {
      toast.error('Failed to update shortlist');
    }
  };

  const handleCreateStudent = async (appId, cohortId) => {
    try {
      await axios.post(`${API}/students?application_id=${appId}&cohort_id=${cohortId}`, {}, auth());
      toast.success('Student enrolled');
      fetchApplications(selectedIntakeId);
    } catch {
      toast.error('Failed to enroll student');
    }
  };

  const handleViewTest = async (appId) => {
    try {
      const res = await axios.get(`${API}/aptitude-tests/application/${appId}`, auth());
      setSelectedTest(res.data);
      setTestDialog(true);
    } catch {
      toast.error('No test found for this application');
    }
  };

  const handleAutoShortlist = async () => {
    if (!selectedIntakeId || selectedIntakeId === 'all') { toast.error('Select a specific intake first'); return; }
    const cutoff = parseFloat(autoShortlistCutoff);
    if (isNaN(cutoff) || cutoff < 0 || cutoff > 100) { toast.error('Cutoff must be 0–100'); return; }
    setAutoShortlisting(true);
    try {
      const res = await axios.post(`${API}/intake-periods/${selectedIntakeId}/auto-shortlist?cutoff=${cutoff}`, {}, auth());
      toast.success(`Shortlisted ${res.data.shortlisted}, declined ${res.data.declined}`);
      setAutoShortlistDialog(false);
      fetchApplications(selectedIntakeId);
    } catch {
      toast.error('Auto-shortlist failed');
    } finally {
      setAutoShortlisting(false);
    }
  };

  const handleBulkSendTestAll = async () => {
    if (!selectedIntakeId || selectedIntakeId === 'all') { toast.error('Select a specific intake first'); return; }
    if (!window.confirm("Send Academic Assessment to all shortlisted applicants who haven't received it yet?")) return;
    setBulkSendingTestAll(true);
    try {
      const res = await axios.post(`${API}/intake-periods/${selectedIntakeId}/bulk-send-test`, {}, auth());
      toast.success(`Assessment links sent to ${res.data.sent} applicant${res.data.sent !== 1 ? 's' : ''}`);
      fetchApplications(selectedIntakeId);
    } catch {
      toast.error('Failed to send assessment links');
    } finally {
      setBulkSendingTestAll(false);
    }
  };

  const openSendTestDialog = () => {
    if (!selectedAppIds.size) { toast.error('Select at least one applicant'); return; }
    const nonShortlisted = Array.from(selectedAppIds)
      .map(id => applications.find(a => a.id === id))
      .filter(a => a && a.shortlisted !== true);
    if (nonShortlisted.length > 0) {
      toast.error(`${nonShortlisted.length} selected applicant${nonShortlisted.length > 1 ? 's are' : ' is'} not shortlisted.`);
      return;
    }
    setSendTestDialog(true);
  };

  const handleSendTestSelected = async () => {
    setSendingTestSelected(true);
    try {
      const res = await axios.post(`${API}/applications/bulk-send-test-selected`, {
        application_ids: Array.from(selectedAppIds),
      }, auth());
      toast.success(`Assessment links sent to ${res.data.sent} applicant${res.data.sent !== 1 ? 's' : ''}${res.data.skipped > 0 ? ` (${res.data.skipped} skipped)` : ''}`);
      setSendTestDialog(false);
      setSelectedAppIds(new Set());
      fetchApplications(selectedIntakeId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send assessment links');
    } finally {
      setSendingTestSelected(false);
    }
  };

  const handleBulkAssignIntake = async () => {
    setAssigningIntake(true);
    try {
      const res = await axios.post(`${API}/applications/bulk-assign-intake`, {
        application_ids: Array.from(selectedAppIds),
        intake_id: assignIntakeId || null,
      }, auth());
      toast.success(`${res.data.updated} application${res.data.updated !== 1 ? 's' : ''} assigned`);
      setAssignIntakeDialog(false);
      setSelectedAppIds(new Set());
      fetchApplications(selectedIntakeId);
    } catch {
      toast.error('Failed to assign intake');
    } finally {
      setAssigningIntake(false);
    }
  };

  const openBulkDecision = (action) => {
    if (!selectedAppIds.size) { toast.error('Select at least one application'); return; }
    setPendingDecision(action);
    setBulkDecisionDialog(true);
  };

  const handleBulkDecision = async () => {
    setProcessingDecision(true);
    try {
      const res = await axios.post(`${API}/applications/bulk-decision`, {
        application_ids: Array.from(selectedAppIds),
        action: pendingDecision,
      }, auth());
      toast.success(res.data.message);
      setBulkDecisionDialog(false);
      setSelectedAppIds(new Set());
      fetchApplications(selectedIntakeId);
    } catch {
      toast.error('Failed to process decision');
    } finally {
      setProcessingDecision(false);
    }
  };

  const handleBulkEnroll = async () => {
    if (!bulkEnrollCohort) { toast.error('Select a cohort'); return; }
    setBulkEnrolling(true);
    try {
      const res = await axios.post(`${API}/intake-periods/${selectedIntakeId}/bulk-enroll?cohort_id=${bulkEnrollCohort}`, {}, auth());
      toast.success(`Enrolled ${res.data.enrolled} students. ${res.data.skipped} already enrolled.`);
      setBulkEnrollDialog(false);
    } catch {
      toast.error('Bulk enroll failed');
    } finally {
      setBulkEnrolling(false);
    }
  };

  const handleCloseReview = async () => {
    if (!selectedIntakeId || selectedIntakeId === 'all') return;
    setClosingReview(true);
    try {
      const res = await axios.post(`${API}/intake-periods/${selectedIntakeId}/close-review?skip_shortlisted=${crSkipShortlisted}&skip_not_shortlisted=${crSkipNotShortlisted}`, {}, auth());
      toast.success(`Review closed. ${res.data.shortlisted_emails_sent} shortlist + ${res.data.declined_emails_sent} decline emails sent.`);
      setCloseReviewDialog(false);
      fetchApplications(selectedIntakeId);
    } catch {
      toast.error('Failed to close review');
    } finally {
      setClosingReview(false);
    }
  };

  const toggleSelect = (id) => setSelectedAppIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const selectedIntake = intakePeriods.find(i => i.id === selectedIntakeId);
  const isReviewing = selectedIntake?.status === 'reviewing';
  const intakeMap = Object.fromEntries(intakePeriods.map(i => [i.id, i.name]));

  const filteredApplications = (() => {
    const PIPELINE_KEYS = ['review','close_review','send_test','awaiting_test','decide','send_outcome','assign_cohort','done'];
    let list = applications.filter(a => {
      if (filterMode === 'flagged')        return a.flagged === true;
      if (filterMode === 'shortlisted')    return a.shortlisted === true;
      if (filterMode === 'declined')       return a.shortlisted === false;
      if (filterMode === 'unreviewed')     return a.shortlisted == null;
      if (filterMode === 'test_completed') return a.status === 'test_completed';
      if (filterMode === 'ineligible')     return a.status === 'ineligible';
      if (filterMode === 'rejected')           return ['not_shortlisted', 'not_selected'].includes(a.status);
      if (filterMode === 'enrolled_confirmed') return a.status === 'enrolled' && a.acceptance_confirmed === true;
      if (filterMode === 'enrolled_pending')   return a.status === 'enrolled' && !a.acceptance_confirmed;
      if (filterMode === 'withdrawn')          return a.status === 'withdrawn';
      if (filterMode === 'rsvp_confirmed') return a.status === 'shortlisted' && a.attendance_rsvp === 'confirmed';
      if (filterMode === 'rsvp_pending')   return a.status === 'shortlisted' && a.attendance_rsvp === 'pending';
      if (PIPELINE_KEYS.includes(filterMode)) return a.next_action?.key === filterMode;
      return true;
    });
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(a =>
        a.full_name?.toLowerCase().includes(q) ||
        a.email?.toLowerCase().includes(q) ||
        a.phone?.toLowerCase().includes(q)
      );
    }
    if (filterMode === 'test_completed') {
      list = [...list].sort((a, b) => (b.aptitude_score ?? -1) - (a.aptitude_score ?? -1));
    }
    return list;
  })();

  const testCompletedCount = applications.filter(a => a.status === 'test_completed').length;

  const selectedAreTestDone = selectedAppIds.size > 0 &&
    Array.from(selectedAppIds).every(id => {
      const app = applications.find(a => a.id === id);
      return app?.status === 'test_completed';
    });

  const sendTestPreview = (() => {
    const selected = Array.from(selectedAppIds).map(id => applications.find(a => a.id === id)).filter(Boolean);
    const willSend = selected.filter(a => ['pending', 'screening', 'received', 'shortlisted'].includes(a.status));
    const willSkip = selected.filter(a => !['pending', 'screening', 'received', 'shortlisted'].includes(a.status));
    return { willSend, willSkip };
  })();

  // Pipeline bucket definitions
  const pipelineBuckets = [
    { value: 'all',           label: 'All',              count: applications.length,          active: 'bg-gray-900 text-white ring-gray-700',       idle: 'bg-white hover:bg-gray-50',       dot: 'bg-gray-400' },
    { value: 'review',        label: 'Awaiting Review',  count: pipelineStats?.awaiting_review ?? 0,      active: 'bg-yellow-500 text-white ring-yellow-400',  idle: 'bg-white hover:bg-yellow-50',     dot: 'bg-yellow-400' },
    { value: 'close_review',  label: 'Close Review',     count: pipelineStats?.close_review_pending ?? 0, active: 'bg-orange-500 text-white ring-orange-400',  idle: 'bg-white hover:bg-orange-50',     dot: 'bg-orange-400' },
    { value: 'send_test',     label: 'Send Assessment',  count: pipelineStats?.send_test_pending ?? 0,    active: 'bg-purple-600 text-white ring-purple-400',  idle: 'bg-white hover:bg-purple-50',     dot: 'bg-purple-400' },
    { value: 'awaiting_test', label: 'Awaiting Assessment', count: pipelineStats?.awaiting_test ?? 0,    active: 'bg-blue-600 text-white ring-blue-400',      idle: 'bg-white hover:bg-blue-50',       dot: 'bg-blue-400' },
    { value: 'decide',        label: 'Needs Decision',   count: pipelineStats?.needs_decision ?? 0,       active: 'bg-indigo-600 text-white ring-indigo-400',  idle: 'bg-white hover:bg-indigo-50',     dot: 'bg-indigo-400' },
    { value: 'send_outcome',  label: 'Send Outcome',     count: pipelineStats?.send_outcome_pending ?? 0,  active: 'bg-green-600 text-white ring-green-400',   idle: 'bg-white hover:bg-green-50',      dot: 'bg-green-400' },
    { value: 'assign_cohort', label: 'Assign Cohort',    count: pipelineStats?.selected_tba_count ?? 0,    active: 'bg-violet-600 text-white ring-violet-400', idle: 'bg-white hover:bg-violet-50',     dot: 'bg-violet-400' },
    { value: 'enrolled_confirmed', label: 'Enrolled — Confirmed', count: applications.filter(a => a.status === 'enrolled' && a.acceptance_confirmed === true).length, active: 'bg-teal-600 text-white ring-teal-400',      idle: 'bg-white hover:bg-teal-50',     dot: 'bg-teal-500' },
    { value: 'enrolled_pending',   label: 'Enrolled — Pending',   count: applications.filter(a => a.status === 'enrolled' && !a.acceptance_confirmed).length,         active: 'bg-amber-500 text-white ring-amber-400',    idle: 'bg-white hover:bg-amber-50',    dot: 'bg-amber-400' },
    { value: 'withdrawn',          label: 'Withdrawn',            count: applications.filter(a => a.status === 'withdrawn').length,                                   active: 'bg-slate-500 text-white ring-slate-400',    idle: 'bg-white hover:bg-slate-50',    dot: 'bg-slate-400' },
    { value: 'flagged',       label: '🚩 Flagged',        count: applications.filter(a => a.flagged).length,                                                active: 'bg-red-600 text-white ring-red-400',        idle: 'bg-white hover:bg-red-50 border-red-200',   dot: 'bg-red-400' },
    { value: 'ineligible',    label: 'Ineligible',        count: applications.filter(a => a.status === 'ineligible').length,                               active: 'bg-gray-500 text-white ring-gray-400',      idle: 'bg-white hover:bg-gray-50',                 dot: 'bg-gray-400' },
    { value: 'rejected',      label: 'Rejected',          count: applications.filter(a => ['not_shortlisted','not_selected'].includes(a.status)).length,    active: 'bg-rose-600 text-white ring-rose-400',      idle: 'bg-white hover:bg-rose-50',                 dot: 'bg-rose-400' },
  ];

  return (
    <div className="min-h-screen bg-gray-50/50">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-playfair font-bold text-primary">Applications</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {selectedIntake ? selectedIntake.name : 'All Intakes'}
              {searchQuery && <span> · "{searchQuery}"</span>}
              {' '}· <strong>{filteredApplications.length}</strong> shown
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => {
              const url = `${API}/export/applications${selectedIntakeId && selectedIntakeId !== 'all' ? `?intake_id=${selectedIntakeId}` : ''}`;
              downloadCsv(url, 'applications.csv');
            }}>
              <Download className="w-3 h-3 mr-1" /> Export CSV
            </Button>
            {isReviewing && (
              <Button size="sm" className="bg-secondary text-white hover:bg-secondary/90" onClick={() => setCloseReviewDialog(true)}>
                Close & Send Emails
              </Button>
            )}
            <select
              className="border rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer"
              value={selectedIntakeId}
              onChange={e => handleIntakeSelect(e.target.value)}
            >
              <option value="all">All Intakes</option>
              {intakePeriods.map(ip => <option key={ip.id} value={ip.id}>{ip.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* ── Tabs ──────────────────────────────────────────────────────────── */}
      <div className="bg-white border-b px-6 flex gap-1">
        <button
          onClick={() => setActiveTab('applications')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'applications' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          Applications
        </button>
        <button
          onClick={() => { setActiveTab('archived'); fetchArchivedApps(); }}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === 'archived' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          <Archive className="w-3.5 h-3.5" /> Archived
          {archivedApps.length > 0 && <span className="bg-gray-200 text-gray-700 text-xs rounded-full px-1.5 py-0.5 leading-none">{archivedApps.length}</span>}
        </button>
        <button
          onClick={() => { setActiveTab('reinvite_pool'); fetchPool(); }}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === 'reinvite_pool' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          <UserPlus className="w-3.5 h-3.5" /> Re-invite Pool
          {pool.filter(p => p.status === 'awaiting_review').length > 0 && (
            <span className="bg-amber-100 text-amber-700 text-xs rounded-full px-1.5 py-0.5 leading-none">
              {pool.filter(p => p.status === 'awaiting_review').length}
            </span>
          )}
        </button>
      </div>

      {activeTab === 'archived' ? (
        <div className="p-6">
          <Card className="overflow-hidden shadow-sm border-0 ring-1 ring-gray-200">
            <CardContent className="p-0">
              {archivedLoading ? (
                <div className="flex items-center justify-center py-20 text-muted-foreground">Loading…</div>
              ) : archivedApps.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-muted-foreground gap-2">
                  <Archive className="w-8 h-8 opacity-30" />
                  <p className="text-sm">No archived applications</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50/80 hover:bg-gray-50/80 border-b">
                        <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Applicant</TableHead>
                        <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Email</TableHead>
                        <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status at Archive</TableHead>
                        <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Archived At</TableHead>
                        <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Archived By</TableHead>
                        <TableHead className="w-28" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {archivedApps.map(a => (
                        <TableRow key={a.id} className="hover:bg-gray-50/60">
                          <TableCell className="font-medium text-sm pl-5">{a.full_name}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{a.email}</TableCell>
                          <TableCell>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[a.status] || 'bg-gray-100 text-gray-600'}`}>
                              {STATUS_LABELS[a.status] || a.status}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {new Date(a.archived_at).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{a.archived_by || '—'}</TableCell>
                          <TableCell className="text-right pr-4">
                            <div className="flex items-center justify-end gap-1">
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                onClick={() => handleRestore(a.id)} title="Restore">
                                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                                onClick={() => handlePermanentDelete(a.id)} title="Permanently delete">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ) : activeTab === 'reinvite_pool' ? (
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Candidates re-invited from previous intakes who have confirmed interest.
            {selectedIntakeId === 'all' && <span className="ml-1 text-amber-600 font-medium">Select a specific intake to view.</span>}
          </p>
          {selectedIntakeId !== 'all' && (
            <button onClick={() => fetchPool()} className="text-xs text-muted-foreground hover:text-primary">Refresh</button>
          )}
        </div>
        {selectedIntakeId === 'all' ? (
          <Card className="overflow-hidden shadow-sm border-0 ring-1 ring-gray-200">
            <CardContent className="p-0">
              <div className="flex flex-col items-center justify-center py-20 text-muted-foreground gap-2">
                <UserPlus className="w-8 h-8 opacity-30" />
                <p className="text-sm">Select a specific intake to view the Re-invite Pool</p>
              </div>
            </CardContent>
          </Card>
        ) : poolLoading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading pool…
          </div>
        ) : pool.length === 0 ? (
          <Card className="overflow-hidden shadow-sm border-0 ring-1 ring-gray-200">
            <CardContent className="p-0">
              <div className="flex flex-col items-center justify-center py-20 text-muted-foreground gap-2">
                <UserPlus className="w-8 h-8 opacity-30" />
                <p className="text-sm">No candidates in the re-invite pool for this intake</p>
                <p className="text-xs">Use "Re-invite to New Intake" on an application to send invitations from previous intakes.</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="overflow-hidden shadow-sm border-0 ring-1 ring-gray-200">
            <CardContent className="p-0">
              <div className="divide-y divide-gray-100">
                {pool.map(entry => (
                  <div key={entry.id} className={`p-4 ${entry.status === 'declined' ? 'opacity-60 bg-gray-50' : entry.status === 'duplicated' ? 'bg-green-50' : 'bg-white hover:bg-gray-50/50'}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm text-primary">{entry.applicant_name}</span>
                          {entry.status === 'duplicated' && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Duplicated</span>}
                          {entry.status === 'declined' && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full font-medium">Declined</span>}
                          {entry.status === 'awaiting_review' && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Awaiting Review</span>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{entry.applicant_email}</p>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-xs text-muted-foreground">
                          <span>From: <strong>{entry.source_intake_name || 'Unknown intake'}</strong></span>
                          {entry.source_status && <span>· Status: {entry.source_status}</span>}
                          {entry.source_score != null && <span>· Profile: <strong>{entry.source_score.toFixed(1)}</strong></span>}
                          {entry.source_aptitude_score != null && <span>· Assessment: <strong>{entry.source_aptitude_score.toFixed(1)}%</strong></span>}
                          {entry.source_final_score != null && <span>· Final: <strong>{entry.source_final_score.toFixed(1)}</strong></span>}
                          {entry.confirmed_at && <span>· Confirmed: {new Date(entry.confirmed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <a href={`/admin/applications/${entry.source_application_id}`} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 border border-blue-200 rounded px-2.5 py-1.5 hover:bg-blue-50">
                          <ExternalLink className="w-3 h-3" /> View Profile
                        </a>
                        {entry.status === 'awaiting_review' && (
                          <>
                            <Button size="sm" className="text-xs h-7 bg-primary hover:bg-primary/90" disabled={poolActionId === entry.id}
                              onClick={() => duplicatePoolEntry(entry.id, entry.applicant_name)}>
                              <UserPlus className="w-3 h-3 mr-1" />
                              {poolActionId === entry.id ? '…' : 'Duplicate'}
                            </Button>
                            <Button size="sm" variant="outline" className="text-xs h-7 border-red-200 text-red-600 hover:bg-red-50"
                              disabled={poolActionId === entry.id} onClick={() => declinePoolEntry(entry.id)}>
                              <X className="w-3 h-3 mr-1" /> Decline
                            </Button>
                          </>
                        )}
                        {entry.status === 'declined' && (
                          <Button size="sm" variant="outline" className="text-xs h-7" disabled={poolActionId === entry.id}
                            onClick={() => undeclinePoolEntry(entry.id)}>
                            Restore
                          </Button>
                        )}
                        {entry.status === 'duplicated' && entry.duplicated_application_id && (
                          <a href={`/admin/applications/${entry.duplicated_application_id}`}
                            className="inline-flex items-center gap-1.5 text-xs text-green-700 border border-green-300 rounded px-2.5 py-1.5 hover:bg-green-50">
                            <ExternalLink className="w-3 h-3" /> View New App
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      ) : (
      <div className="p-6 space-y-4">

        {/* ── Pipeline bucket filters ──────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {pipelineBuckets.map(({ value, label, count, active, idle, dot }) => (
            <button
              key={value}
              onClick={() => { setFilterMode(value); setSelectedAppIds(new Set()); }}
              className={`rounded-xl border p-3 text-left transition-all shadow-sm ring-offset-1 ${filterMode === value ? `${active} ring-2` : idle}`}
            >
              <div className="flex items-center gap-1.5 mb-2">
                <span className={`w-2 h-2 rounded-full shrink-0 ${filterMode === value ? 'bg-white/70' : dot}`} />
                {pipelineStats?.overdue > 0 && value === 'awaiting_test' && (
                  <span className="text-xs font-bold text-amber-600 ml-auto">⚠</span>
                )}
              </div>
              <div className="text-xl font-bold leading-none">{count}</div>
              <div className="text-xs mt-1 opacity-70 font-medium leading-snug">{label}</div>
            </button>
          ))}
        </div>
        {/* ── RSVP split card (only when there are shortlisted apps) ─────── */}
        {(() => {
          const rsvpConfirmed = applications.filter(a => a.status === 'shortlisted' && a.attendance_rsvp === 'confirmed').length;
          const rsvpPending   = applications.filter(a => a.status === 'shortlisted' && (a.attendance_rsvp === 'pending' || !a.attendance_rsvp)).length;
          if (rsvpConfirmed + rsvpPending === 0) return null;
          return (
            <div className="rounded-xl border shadow-sm overflow-hidden flex">
              <button
                onClick={() => { setFilterMode('rsvp_confirmed'); setSelectedAppIds(new Set()); }}
                className={`flex-1 p-3 text-left transition-all ${filterMode === 'rsvp_confirmed' ? 'bg-teal-600 text-white ring-2 ring-teal-400 ring-offset-1' : 'bg-white hover:bg-teal-50'}`}
              >
                <div className="flex items-center gap-1.5 mb-2">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${filterMode === 'rsvp_confirmed' ? 'bg-white/70' : 'bg-teal-400'}`} />
                </div>
                <div className="text-xl font-bold leading-none">{rsvpConfirmed}</div>
                <div className="text-xs mt-1 opacity-70 font-medium">RSVP Confirmed</div>
              </button>
              <div className="w-px bg-gray-200" />
              <button
                onClick={() => { setFilterMode('rsvp_pending'); setSelectedAppIds(new Set()); }}
                className={`flex-1 p-3 text-left transition-all ${filterMode === 'rsvp_pending' ? 'bg-amber-500 text-white ring-2 ring-amber-400 ring-offset-1' : 'bg-white hover:bg-amber-50'}`}
              >
                <div className="flex items-center gap-1.5 mb-2">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${filterMode === 'rsvp_pending' ? 'bg-white/70' : 'bg-amber-400'}`} />
                </div>
                <div className="text-xl font-bold leading-none">{rsvpPending}</div>
                <div className="text-xs mt-1 opacity-70 font-medium">RSVP Pending</div>
              </button>
            </div>
          );
        })()}

        {pipelineStats?.overdue > 0 && (
          <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <span className="font-bold">⚠</span>
            <span><strong>{pipelineStats.overdue}</strong> application{pipelineStats.overdue > 1 ? 's are' : ' is'} overdue and may need attention.</span>
          </div>
        )}

        {/* ── Toolbar: search + intake actions ────────────────────────────── */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Input
              placeholder="Search by name, email or phone…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-72 h-8 text-sm pr-8"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          <div className="ml-auto flex items-center gap-2 flex-wrap">
            {selectedIntakeId !== 'all' && !selectedAreTestDone && (
              <>
                <Button size="sm" variant="outline" onClick={() => setAutoShortlistDialog(true)}>
                  <Star className="w-3 h-3 mr-1" /> Auto-Shortlist
                </Button>
                <Button size="sm" variant="outline" onClick={handleBulkSendTestAll} disabled={bulkSendingTestAll}>
                  {bulkSendingTestAll ? 'Sending…' : <><Send className="w-3 h-3 mr-1" />Send to All Shortlisted</>}
                </Button>
                {cohorts.length > 0 && (
                  <Button size="sm" variant="outline" onClick={() => { setBulkEnrollCohort(cohorts[0]?.id || ''); setBulkEnrollDialog(true); }}>
                    <CheckCircle className="w-3 h-3 mr-1" /> Bulk Enroll
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Test done contextual hint */}
        {filterMode === 'test_completed' && testCompletedCount > 0 && (
          <div className="px-4 py-2.5 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-800 flex items-center gap-2">
            <CheckSquare className="w-4 h-4 shrink-0" />
            Sorted by aptitude score (highest first). Open each applicant to review their assessment before making a decision.
          </div>
        )}

        {/* ── Table ────────────────────────────────────────────────────────── */}
        <Card className="overflow-hidden shadow-sm border-0 ring-1 ring-gray-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-20 text-muted-foreground">Loading…</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50/80 hover:bg-gray-50/80 border-b">
                      <TableHead className="w-10 pl-5">
                        <input type="checkbox" className="cursor-pointer"
                          checked={filteredApplications.length > 0 && filteredApplications.every(a => selectedAppIds.has(a.id))}
                          onChange={() => setSelectedAppIds(
                            filteredApplications.every(a => selectedAppIds.has(a.id))
                              ? new Set()
                              : new Set(filteredApplications.map(a => a.id))
                          )} />
                      </TableHead>
                      <TableHead className="w-8 text-xs text-muted-foreground">#</TableHead>
                      <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Applicant</TableHead>
                      <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Score</TableHead>
                      <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Assessment</TableHead>
                      <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wide text-center">Eligible</TableHead>
                      <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wide text-center">Shortlisted</TableHead>
                      {selectedIntakeId === 'all' && (
                        <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Intake</TableHead>
                      )}
                      <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</TableHead>
                      <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Next Step</TableHead>
                      <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Submitted</TableHead>
                      <TableHead className="w-28 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredApplications.map((app, idx) => {
                      const initials = app.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '??';
                      return (
                        <TableRow
                          key={app.id}
                          className={`cursor-pointer group transition-colors ${selectedAppIds.has(app.id) ? 'bg-indigo-50 hover:bg-indigo-100/80' : 'hover:bg-blue-50/30'}`}
                          onClick={() => {
                            const ids = filteredApplications.map(a => a.id);
                            sessionStorage.setItem('reviewQueue', JSON.stringify({ applicationIds: ids, returnIntakeId: selectedIntakeId }));
                            navigate(`/admin/applications/${app.id}`, { state: { returnIntakeId: selectedIntakeId, applicationIds: ids, currentIndex: idx } });
                          }}
                        >
                          {/* Checkbox */}
                          <TableCell className="pl-5" onClick={e => e.stopPropagation()}>
                            <input type="checkbox" className="cursor-pointer"
                              checked={selectedAppIds.has(app.id)} onChange={() => toggleSelect(app.id)} />
                          </TableCell>

                          {/* Rank */}
                          <TableCell className="text-xs text-muted-foreground">{idx + 1}</TableCell>

                          {/* Applicant: initials avatar + name + email */}
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0 select-none">
                                {initials}
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <p className="font-medium text-sm leading-snug truncate">{app.full_name}</p>
                                  {app.flagged && <span className="text-red-500 shrink-0" title={app.flag_reason}>🚩</span>}
                                </div>
                                <p className="text-xs text-muted-foreground truncate">{app.email}</p>
                              </div>
                            </div>
                          </TableCell>

                          {/* Consolidated score */}
                          <TableCell>
                            {app.final_score != null ? (
                              <div>
                                <span className={`inline-block text-sm font-bold px-2 py-0.5 rounded-md ${app.final_score >= 70 ? 'bg-green-100 text-green-700' : app.final_score >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-600'}`}>
                                  {app.final_score.toFixed(1)}
                                </span>
                                <p className="text-xs text-muted-foreground mt-1">
                                  App {app.score?.toFixed(1) ?? '—'} · Test {app.aptitude_score != null ? `${app.aptitude_score.toFixed(0)}%` : '—'}
                                </p>
                              </div>
                            ) : app.score != null ? (
                              <span className={`text-sm font-bold ${app.score >= 70 ? 'text-green-600' : app.score >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                                {app.score.toFixed(1)}
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </TableCell>

                          {/* Assessment activity */}
                          <TableCell>
                            {(() => {
                              const ts = testSummaries[app.id];
                              if (!ts) return <span className="text-xs text-muted-foreground">—</span>;
                              if (!ts.completed) {
                                const label = ts.token_status === 'active' ? 'In progress' : 'Pending';
                                return <span className="text-xs text-muted-foreground italic">{label}</span>;
                              }
                              const time = formatTestTime(ts.active_seconds ?? ts.wall_seconds);
                              const v = ts.violation_count;
                              const INTEGRITY = {
                                clean:      { label: 'Clean',      cls: 'text-green-600' },
                                watch:      { label: 'Watch',      cls: 'text-amber-500' },
                                suspicious: { label: 'Suspicious', cls: 'text-orange-600' },
                                flagged:    { label: 'Flagged',    cls: 'text-red-600' },
                              };
                              const lvl = INTEGRITY[ts.integrity_level];
                              return (
                                <div className="leading-tight space-y-0.5">
                                  <div>
                                    {time && <span className="text-xs text-gray-700">{time}</span>}
                                    {v > 0 && (
                                      <span className={`ml-1.5 text-xs font-medium ${v >= 3 ? 'text-red-600' : 'text-amber-600'}`}>
                                        · {v} viol
                                      </span>
                                    )}
                                  </div>
                                  {lvl && (
                                    <span className={`text-[11px] font-medium ${lvl.cls}`}>{lvl.label}</span>
                                  )}
                                </div>
                              );
                            })()}
                          </TableCell>

                          {/* Eligible */}
                          <TableCell className="text-center">
                            {app.eligible
                              ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                              : <XCircle className="w-4 h-4 text-red-400 mx-auto" />}
                          </TableCell>

                          {/* Shortlisted toggle */}
                          <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                            <button onClick={() => handleToggleShortlist(app.id, app.shortlisted)} className="focus:outline-none mx-auto block">
                              {app.shortlisted === true
                                ? <Star className="w-5 h-5 text-yellow-500 fill-yellow-400 mx-auto" />
                                : app.shortlisted === false
                                ? <XCircle className="w-5 h-5 text-red-400 mx-auto" />
                                : <AlertCircle className="w-5 h-5 text-gray-300 mx-auto" />}
                            </button>
                          </TableCell>

                          {/* Intake */}
                          {selectedIntakeId === 'all' && (
                            <TableCell>
                              {app.intake_id ? (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium whitespace-nowrap">
                                  {intakeMap[app.intake_id] || 'Unknown'}
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground italic">Unassigned</span>
                              )}
                            </TableCell>
                          )}

                          {/* Status badge */}
                          <TableCell>
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${STATUS_COLORS[app.status] || 'bg-gray-100 text-gray-600'}`}>
                              {STATUS_LABELS[app.status] || app.status}
                            </span>
                          </TableCell>

                          {/* Next Step badge */}
                          <TableCell onClick={e => e.stopPropagation()}>
                            {app.next_action && app.next_action.key !== 'done' && (
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border whitespace-nowrap ${NEXT_ACTION_STYLES[app.next_action.key]?.bg || 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                                {app.overdue_days > 0 && <span title="Overdue">⚠</span>}
                                {app.next_action.label}
                              </span>
                            )}
                            {app.next_action?.key === 'done' && (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>

                          {/* Submitted date */}
                          <TableCell>
                            <span className="text-xs text-muted-foreground">
                              {new Date(app.submitted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                          </TableCell>

                          {/* Actions — revealed on row hover */}
                          <TableCell onClick={e => e.stopPropagation()}>
                            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Edit"
                                onClick={() => { setEditForm({ id: app.id, email: app.email, status: app.status, score: app.score != null ? String(app.score) : '', final_score: app.final_score != null ? String(app.final_score) : '', app_score_display: app.score != null ? app.score.toFixed(2) : '—' }); setEditDialog(true); }}>
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              {['test_sent', 'test_invited', 'test_completed', 'accepted', 'rejected', 'not_selected', 'enrolled'].includes(app.status) && (
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="View Test" onClick={() => handleViewTest(app.id)}>
                                  <FileText className="w-3.5 h-3.5 text-indigo-500" />
                                </Button>
                              )}
                              {app.status === 'accepted' && cohorts.length > 0 && (
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Enroll"
                                  onClick={() => handleCreateStudent(app.id, cohorts[0].id)}>
                                  <UserCheck className="w-3.5 h-3.5 text-teal-600" />
                                </Button>
                              )}
                              {app.status === 'enrolled' && (
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-slate-700 hover:bg-slate-50" title="Mark as Withdrawn"
                                  onClick={() => handleWithdraw(app)}>
                                  <XCircle className="w-3.5 h-3.5" />
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50" title="Delete"
                                onClick={() => handleDelete(app.id)}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50 ml-0.5 shrink-0" />
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {filteredApplications.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={10}>
                          <div className="flex flex-col items-center justify-center py-16 text-center">
                            <FileText className="w-10 h-10 text-muted-foreground/30 mb-3" />
                            <p className="font-medium text-muted-foreground">
                              {filterMode !== 'all' ? 'No applications in this category' : 'No applications found'}
                            </p>
                            {searchQuery && <p className="text-sm text-muted-foreground mt-1">Try a different search term</p>}
                            {filterMode !== 'all' && (
                              <button className="text-xs text-primary mt-2 hover:underline" onClick={() => setFilterMode('all')}>
                                Clear filter
                              </button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      )} {/* end activeTab === 'applications' */}

      {/* ── Floating Bulk Action Bar ─────────────────────────────────────────── */}
      {selectedAppIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <div className="bg-gray-900 text-white rounded-2xl shadow-2xl border border-white/10 px-5 py-3 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">
                {selectedAppIds.size}
              </div>
              <span className="text-sm font-medium">selected</span>
            </div>

            <div className="w-px h-5 bg-white/20" />

            {!selectedAreTestDone && (
              <Button size="sm"
                className="border-white/25 text-white bg-white/10 hover:bg-white/20 h-7 text-xs border"
                onClick={openSendTestDialog}>
                <Send className="w-3 h-3 mr-1" /> Send Assessment
              </Button>
            )}


            {!selectedAreTestDone && (
              <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={handleBulkDelete}>
                <Trash2 className="w-3 h-3 mr-1" /> Delete
              </Button>
            )}

            <Button size="sm"
              className="border-white/25 text-white bg-white/10 hover:bg-white/20 h-7 text-xs border"
              onClick={() => { setAssignIntakeId(selectedIntakeId !== 'all' ? selectedIntakeId : ''); setAssignIntakeDialog(true); }}>
              Assign Intake
            </Button>

            <div className="w-px h-5 bg-white/20" />

            <button onClick={() => setSelectedAppIds(new Set())} className="text-white/50 hover:text-white transition-colors" title="Deselect all">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── Bulk Assign Intake Dialog ────────────────────────────────────────── */}
      <Dialog open={assignIntakeDialog} onOpenChange={setAssignIntakeDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Assign to Intake</DialogTitle>
            <DialogDescription>
              Assign {selectedAppIds.size} selected application{selectedAppIds.size !== 1 ? 's' : ''} to an intake period.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label>Intake Period</Label>
            <select className="w-full border rounded px-3 py-2 text-sm mt-1 bg-white"
              value={assignIntakeId}
              onChange={e => setAssignIntakeId(e.target.value)}>
              <option value="">— Unassign (remove from intake) —</option>
              {intakePeriods.map(ip => <option key={ip.id} value={ip.id}>{ip.name}</option>)}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignIntakeDialog(false)}>Cancel</Button>
            <Button onClick={handleBulkAssignIntake} disabled={assigningIntake}>
              {assigningIntake ? 'Assigning…' : 'Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Send Test (Selected) Confirmation Dialog ─────────────────────────── */}
      <Dialog open={sendTestDialog} onOpenChange={setSendTestDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Send Academic Assessment Link</DialogTitle>
            <DialogDescription>Review who will receive the assessment link from your selection.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="flex items-center justify-between px-3 py-2 bg-green-50 rounded border border-green-200">
              <span className="text-sm text-green-800">Will receive link</span>
              <span className="font-bold text-green-700">{sendTestPreview.willSend.length}</span>
            </div>
            {sendTestPreview.willSkip.length > 0 && (
              <div className="flex items-center justify-between px-3 py-2 bg-yellow-50 rounded border border-yellow-200">
                <span className="text-sm text-yellow-800">Already sent — skipped</span>
                <span className="font-bold text-yellow-700">{sendTestPreview.willSkip.length}</span>
              </div>
            )}
            {sendTestPreview.willSend.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-2">All selected applicants have already been sent an assessment link.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendTestDialog(false)}>Cancel</Button>
            <Button onClick={handleSendTestSelected} disabled={sendingTestSelected || sendTestPreview.willSend.length === 0}>
              {sendingTestSelected ? 'Sending…' : `Send to ${sendTestPreview.willSend.length}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Bulk Decision Confirmation Dialog ────────────────────────────────── */}
      <Dialog open={bulkDecisionDialog} onOpenChange={setBulkDecisionDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {pendingDecision === 'accept' ? 'Accept Selected Applicants' : 'Reject Selected Applicants'}
            </DialogTitle>
            <DialogDescription>
              {pendingDecision === 'accept'
                ? `This will mark ${selectedAppIds.size} applicant${selectedAppIds.size !== 1 ? 's' : ''} as accepted and send each an acceptance email.`
                : `This will mark ${selectedAppIds.size} applicant${selectedAppIds.size !== 1 ? 's' : ''} as rejected and send each a test result email.`}
              {' '}This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDecisionDialog(false)}>Cancel</Button>
            <Button
              className={pendingDecision === 'accept' ? 'bg-green-600 hover:bg-green-700 text-white' : ''}
              variant={pendingDecision === 'reject' ? 'destructive' : 'default'}
              onClick={handleBulkDecision}
              disabled={processingDecision}
            >
              {processingDecision ? 'Processing…' : pendingDecision === 'accept' ? `Accept ${selectedAppIds.size}` : `Reject ${selectedAppIds.size}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Edit Application Dialog ───────────────────────────────────────────── */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Application</DialogTitle>
            <DialogDescription>Override application fields.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Email</Label>
              <Input value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} /></div>
            <div><Label>Status</Label>
              <select className="w-full border rounded px-3 py-2 text-sm bg-white" value={editForm.status}
                onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                {['received','ineligible','shortlisted','not_shortlisted','test_invited','test_completed','accepted','not_selected','on_hold','enrolled'].map(s => (
                  <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
                ))}
              </select></div>
            <div>
              <Label>Application Score (academic, read-only)</Label>
              <Input value={editForm.app_score_display} disabled className="bg-gray-50 text-gray-500" />
            </div>
            <div>
              <Label>Final Score Override (0–100)</Label>
              <Input type="number" min="0" max="100" step="0.01" value={editForm.final_score}
                onChange={e => setEditForm(f => ({ ...f, final_score: e.target.value }))}
                placeholder="Leave blank to keep current" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEditSave}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Auto-Shortlist Dialog ────────────────────────────────────────────── */}
      <Dialog open={autoShortlistDialog} onOpenChange={setAutoShortlistDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Auto-Shortlist by Score</DialogTitle>
            <DialogDescription>Shortlist all eligible applicants at or above the cutoff. Others are marked declined.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <Label>Score Cutoff (0–100)</Label>
            <Input type="number" min="0" max="100" value={autoShortlistCutoff}
              onChange={e => setAutoShortlistCutoff(e.target.value)} />
            <p className="text-xs text-muted-foreground">Applicants scoring ≥ {autoShortlistCutoff || '?'} will be shortlisted.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAutoShortlistDialog(false)}>Cancel</Button>
            <Button onClick={handleAutoShortlist} disabled={autoShortlisting}>
              {autoShortlisting ? 'Shortlisting…' : `Shortlist at ≥ ${autoShortlistCutoff}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Bulk Enroll Dialog ───────────────────────────────────────────────── */}
      <Dialog open={bulkEnrollDialog} onOpenChange={setBulkEnrollDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Bulk Enroll Accepted Applicants</DialogTitle>
            <DialogDescription>Enroll all accepted applicants from this intake into a cohort.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label>Select Cohort</Label>
            <select className="w-full border rounded px-3 py-2 text-sm mt-1" value={bulkEnrollCohort}
              onChange={e => setBulkEnrollCohort(e.target.value)}>
              <option value="">— Select cohort —</option>
              {cohorts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkEnrollDialog(false)}>Cancel</Button>
            <Button onClick={handleBulkEnroll} disabled={bulkEnrolling || !bulkEnrollCohort}>
              {bulkEnrolling ? 'Enrolling…' : 'Enroll All Accepted'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Close Review Dialog ──────────────────────────────────────────────── */}
      <Dialog open={closeReviewDialog} onOpenChange={setCloseReviewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Close Review & Send All Emails</DialogTitle>
            <DialogDescription>Send shortlist / decline emails to all applicants. This cannot be undone.</DialogDescription>
          </DialogHeader>
          {intakeStats && (
            <div className="grid grid-cols-3 gap-3 text-center my-2">
              <div className="bg-green-50 p-3 rounded"><div className="text-2xl font-bold text-green-700">{intakeStats.shortlisted}</div><div className="text-xs">Shortlisted</div></div>
              <div className="bg-red-50 p-3 rounded"><div className="text-2xl font-bold text-red-700">{intakeStats.declined}</div><div className="text-xs">Declined</div></div>
              <div className="bg-yellow-50 p-3 rounded"><div className="text-2xl font-bold text-yellow-700">{intakeStats.pending_review}</div><div className="text-xs">Unreviewed</div></div>
            </div>
          )}
          <div className="space-y-2 border rounded-lg p-3 bg-slate-50 my-2">
            <p className="text-xs font-medium text-slate-600 mb-2">Duplicate email handling:</p>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" className="mt-0.5 cursor-pointer" checked={crSkipShortlisted} onChange={e => setCrSkipShortlisted(e.target.checked)} />
              <span className="text-sm">
                <span className="font-medium">Skip Shortlisted emails already sent</span>
                <span className="text-muted-foreground text-xs block">Uncheck to resend to all starred applicants</span>
              </span>
            </label>
            <label className="flex items-start gap-2.5 cursor-pointer mt-1">
              <input type="checkbox" className="mt-0.5 cursor-pointer" checked={crSkipNotShortlisted} onChange={e => setCrSkipNotShortlisted(e.target.checked)} />
              <span className="text-sm">
                <span className="font-medium">Skip Not Shortlisted emails already sent</span>
                <span className="text-muted-foreground text-xs block">Uncheck to resend to all non-starred applicants</span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseReviewDialog(false)}>Cancel</Button>
            <Button className="bg-secondary text-white hover:bg-secondary/90" onClick={handleCloseReview} disabled={closingReview}>
              {closingReview ? 'Sending…' : 'Confirm & Send All Emails'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── View Test Responses Dialog ───────────────────────────────────────── */}
      <Dialog open={testDialog} onOpenChange={setTestDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Academic Assessment Responses</DialogTitle>
            <DialogDescription>
              {selectedTest?.completed ? `Score: ${selectedTest.score?.toFixed(1)}% · Completed ${new Date(selectedTest.completed_at).toLocaleString()}` : 'Not yet completed'}
            </DialogDescription>
          </DialogHeader>
          {selectedTest && (
            <div className="space-y-6">
              {selectedTest.questions?.map((q, qi) => {
                const answer = selectedTest.answers?.find(a => a.question_id === q.id);
                return (
                  <div key={q.id} className="border rounded p-4">
                    <p className="font-medium text-sm mb-3">Q{qi + 1}. {q.question}</p>
                    {q.type === 'multiple_choice' ? (
                      <div className="space-y-1">
                        {q.options.map((opt, oi) => (
                          <div key={oi} className={`px-3 py-1.5 rounded text-sm flex items-center gap-2 ${
                            oi === q.correct_answer ? 'bg-green-100 text-green-800 font-medium' :
                            answer?.selected_answer === oi ? 'bg-red-100 text-red-700' : 'bg-gray-50'
                          }`}>
                            {oi === q.correct_answer ? <CheckCircle className="w-3 h-3" /> :
                             answer?.selected_answer === oi ? <XCircle className="w-3 h-3" /> : <span className="w-3 h-3" />}
                            {opt}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-gray-50 rounded p-3 text-sm text-gray-700 whitespace-pre-line">
                        {answer?.essay_answer || <span className="italic text-muted-foreground">No answer provided</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
